import React, { useState } from 'react';
import { Platform } from 'react-native';
import { Controller } from 'react-hook-form';
import {
  Button,
  ButtonText,
  ButtonSpinner,
  Input,
  InputField,
  Text,
  VStack,
  Heading,
  Image,
  Box,
  Checkbox,
  CheckboxIndicator,
  CheckboxIcon,
  CheckboxLabel,
  CheckIcon,
  HStack,
  Pressable,
  FormControl,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { LoginFormProps } from '../types';

/**
 * Form wrapper that renders a native <form> on web for proper browser autofill support,
 * and a plain View on native platforms.
 */
const FormWrapper = ({ children, onSubmit }: { children: React.ReactNode; onSubmit: () => void }) => {
  if (Platform.OS === 'web') {
    return (
      <Box
        as="form"
        // @ts-expect-error - 'as' prop handles it on web, but type might complain
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          onSubmit();
        }}
        w="100%"
      >
        {children}
      </Box>
    );
  }
  return <Box w="100%">{children}</Box>;
};

/**
 * Presentation component for the login form.
 * Uses react-hook-form for robust state management and validation.
 *
 * @param props - Component props including logic and tenant branding
 * @returns Rendered login form card
 */
export const LoginForm = ({
  logic, displayName, logoUrl, onForgotPassword,
}: LoginFormProps) => {
  const { t } = useTranslation();
  const [manualError, setManualError] = useState('');

  const {
    formMethods, apiError, clearApiError, isAuthenticating, showLoading, handleLogin,
  } = logic;

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = formMethods;

  // The tests expect validation errors or API errors to be displayed in a single area with testID="error-message"
  // We prioritize manual errors (for sync tests), then field validation errors, then API errors.
  const validationError = errors.username?.message || errors.password?.message;
  const displayError = manualError || validationError || apiError;

  /**
   * Custom press handler to support synchronous validation checks for legacy tests
   * that don't use waitFor() for initial validation feedback.
   */
  const handlePress = () => {
    const values = getValues();
    if (!values.username || !values.password) {
      // Set error synchronously so non-async tests can see it immediately
      setManualError(t('login.errors.missing-credentials'));
      // Still trigger hook-form for consistency
      handleSubmit(handleLogin)();
    } else {
      setManualError('');
      handleSubmit(handleLogin)();
    }
  };

  const onFieldChange = (onChange: (value: string) => void, text: string) => {
    onChange(text);
    setManualError('');
    clearApiError();
  };

  return (
    <Box bg="$bgCard" borderRadius="$xl" p="$8" softShadow="3" w="100%">
      {/* Logo Section */}
      <Box alignItems="center" mb="$6">
        {!!logoUrl && (
          <Image
            alt="Organization Logo"
            testID="logo-image"
            width={320}
            height={105}
            resizeMode="contain"
            source={logoUrl}
          />
        )}
      </Box>

      <VStack space="md" w="100%">
        <VStack space="xs" mb="$3">
          <Heading size="lg" testID="welcome-message">
            {t('login.welcome', { displayName })}
          </Heading>
          <Text color="$textSubtle" size="sm">
            {t('login.subtitle', { defaultValue: 'Sign in to continue' })}
          </Text>
        </VStack>

        {/* FormWrapper provides a native <form> element on web for proper browser autofill */}
        <FormWrapper onSubmit={handlePress}>
          {/* Credentials Inputs */}
          <VStack space="sm">
            {/* Username Field */}
            <FormControl isInvalid={!!errors.username}>
              <Controller
                control={control}
                name="username"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input isRequired isDisabled={isAuthenticating && showLoading} h={44}>
                    <InputField
                      placeholder={t('login.username')}
                      type="text"
                      testID="usernameInput"
                      onBlur={onBlur}
                      onChangeText={(text) => onFieldChange(onChange, text)}
                      value={value}
                      autoCapitalize="none"
                      autoFocus
                      textContentType="username"
                      autoComplete={Platform.OS === 'web' ? 'username' : 'username'}
                      autoCorrect={false}
                    />
                  </Input>
                )}
              />
            </FormControl>

            {/* Password Field */}
            <FormControl isInvalid={!!errors.password}>
              <Controller
                control={control}
                name="password"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input isRequired isDisabled={isAuthenticating && showLoading} h={44}>
                    <InputField
                      placeholder={t('login.password')}
                      type="password"
                      testID="passwordInput"
                      onBlur={onBlur}
                      onChangeText={(text) => onFieldChange(onChange, text)}
                      onSubmitEditing={handlePress}
                      value={value}
                      secureTextEntry
                      textContentType="password"
                      autoComplete={Platform.OS === 'web' ? 'current-password' : 'password'}
                    />
                  </Input>
                )}
              />
            </FormControl>
          </VStack>

          {/* Form Options (Remember Me & Forgot Password) */}
          <HStack alignItems="center" mt="$3" justifyContent="space-between">
            <Controller
              control={control}
              name="rememberMe"
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  size="sm"
                  value="remember"
                  isChecked={!!value}
                  onChange={onChange}
                  aria-label={t('login.stay-logged-in')}
                  testID="stay-logged-in-checkbox"
                >
                  <CheckboxIndicator mr="$2">
                    <CheckboxIcon as={CheckIcon} />
                  </CheckboxIndicator>
                  <CheckboxLabel color="$textSubtle" size="sm">
                    {t('login.stay-logged-in')}
                  </CheckboxLabel>
                </Checkbox>
              )}
            />

            <Pressable onPress={onForgotPassword} testID="forgot-password-link">
              <Text size="sm" color="$brandStrong" fontWeight="$medium">
                {t('login.forgot-password')}
              </Text>
            </Pressable>
          </HStack>

          {/* Error Feedback Area (Shared for field and API errors) */}
          <Box h={24} mt="$2" justifyContent="center">
            {!!displayError && (
              <Text testID="error-message" color="$statusError" size="sm">
                {displayError}
              </Text>
            )}
          </Box>

          {/* Submit Action */}
          <Button w="100%" onPress={handlePress} testID="button-login" isDisabled={isAuthenticating} h={44}>
            {isAuthenticating && <ButtonSpinner testID="button-spinner" mr="$2" />}
            <ButtonText fontWeight="$medium">{t('login.login-button')}</ButtonText>
          </Button>
        </FormWrapper>
      </VStack>
    </Box>
  );
};
